import { type RouteLocationNormalized, type RouteRecordNormalized } from 'vue-router'
import type { App, Plugin } from 'vue'
import { unref } from 'vue'
import { isObject } from '/@/neolix-utils/src/index'
import { useUserStoreWithOut } from '/@/store/modules/user'
import { USER_CONFIG_KEY } from '/@/enums/cacheEnum'
import type { UserConfig } from '/#/store'
import { useGlobSetting } from '/@/hooks/setting'
import { useDebounceFn } from '@vueuse/core'

export const noop = () => {}
/**
 * @description:  Set ui mount node
 */
export function getPopupContainer(node?: HTMLElement): HTMLElement {
  return (node?.parentNode as HTMLElement) ?? document.body
}

/**
 * Add the object as a parameter to the URL
 * @param baseUrl url
 * @param obj
 * @returns {string}
 * eg:
 *  let obj = {a: '3', b: '4'}
 *  setObjToUrlParams('www.baidu.com', obj)
 *  ==>www.baidu.com?a=3&b=4
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  let parameters = ''
  for (const key in obj) {
    parameters += key + '=' + encodeURIComponent(obj[key]) + '&'
  }
  parameters = parameters.replace(/&$/, '')
  return /\?$/.test(baseUrl) ? baseUrl + parameters : baseUrl.replace(/\/?$/, '?') + parameters
}

export function deepMerge<T = any>(src: any = {}, target: any = {}): T {
  let key: string
  for (key in target) {
    src[key] = isObject(src[key]) ? deepMerge(src[key], target[key]) : (src[key] = target[key])
  }
  return src
}

export function openWindow(
  url: string,
  opt?: { target?: TargetContext | string; noopener?: boolean; noreferrer?: boolean },
) {
  const { target = '__blank', noopener = true, noreferrer = true } = opt || {}
  const feature: string[] = []

  noopener && feature.push('noopener=yes')
  noreferrer && feature.push('noreferrer=yes')

  window.open(url, target, feature.join(','))
}

// dynamic use hook props
export function getDynamicProps<T, U>(props: T): Partial<U> {
  const ret: Recordable = {}

  Object.keys(props).map((key) => {
    ret[key] = unref((props as Recordable)[key])
  })

  return ret as Partial<U>
}

export function getRawRoute(route: RouteLocationNormalized): RouteLocationNormalized {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path,
        }))
      : undefined) as RouteRecordNormalized[],
  }
}

export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: App) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param {string} url
 * @returns {Object}
 */
export function getQueryObject(url?: string): any {
  url = url == null ? window.location.href : url
  const search = url.substring(url.lastIndexOf('?') + 1)
  const obj = {}
  const reg = /([^?&=]+)=([^?&=]*)/g
  search.replace(reg, (rs, $1, $2) => {
    const name = decodeURIComponent($1)
    let val = decodeURIComponent($2)
    val = String(val)
    // 特殊case：pageId=80923640&token=123&token=345, 取最后的token
    obj[name] = val
    return rs
  })
  return obj
}

export function getConfig(name: string): any {
  const userStore = useUserStoreWithOut()
  const userConfigVal = userStore.getUserConfig
  return (
    userConfigVal[name] || {
      data: {},
    }
  )
}

export const setConfig = useDebounceFn(async (params: UserConfig) => {
  const { userConfigUseCache } = useGlobSetting()
  const userStore = useUserStoreWithOut()
  const configChangeData = Object.assign({}, userStore.getUserConfig, params)
  userStore.setUserConfig(configChangeData)
  if (userConfigUseCache === 'true') {
    localStorage.setItem(USER_CONFIG_KEY, JSON.stringify(configChangeData))
  }
}, 500)

// 处理外部登录跳转（SSO 或飞连）
export const toLogin = () => {
  const globSetting = useGlobSetting()

  const hasSSO = globSetting.loginTypeSSO === 'true'
  const hasFL = globSetting.loginTypeFL === 'true'

  if (hasSSO) {
    // SSO 登录逻辑
    let redirectUrl = location.href
    const urlObj = new URL(location.href)
    // url登录失败，并且包含token，证明该token失效，先去掉token再跳到passport登录页
    if (urlObj.searchParams.has('token')) {
      urlObj.searchParams.delete('token')
      redirectUrl = urlObj.href
    }
    // 如果url上有token参数，需要处理掉，避免pass获取到过期的token (new URL 不会处理hash中的参数)
    const tokenReg = /[&]?token=[^&#?]+/g
    redirectUrl = redirectUrl.replace(tokenReg, '')
    const openUrl = `${globSetting.passportUrl}/authorize?response_type=token&client_id=${
      globSetting.appId
    }&redirect_uri=${encodeURIComponent(redirectUrl)}`
    openWindow(openUrl, { target: '_self' })
  } else if (hasFL) {
    // 飞连登录逻辑（待实现）
    // const flLoginUrl = `${globSetting.flLoginUrl}?redirect_uri=${encodeURIComponent(location.href)}`
    // openWindow(flLoginUrl, { target: '_self' })
  }
}

// emoji正则
const emojiRegex =
  /[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}]|\p{Emoji_Presentation}/gu

// emoji替换
export const replaceEmoji = (str) => {
  return str?.replace(emojiRegex, '')
}
